import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { InputTextarea } from 'primereact/inputtextarea';
import { Tooltip } from 'primereact/tooltip';
import { BOMFormulaFunctionEntity, BOMFormulaVariableEntity } from '../../../interface/settings/bom-mapping';
import { IsNullOrEmptyArray, IsStringNullEmptyOrUndefined } from '../../../utils/null-check';
import { OPERATOR_CHIPS, PillChip, TEST_SCENARIOS, parseBOMFormula, safeEvaluateFormula } from './bom-utils';
import './bom-mapping.css';

/** the preview's verdict on a formula: the quantity it gives the standard door, and why it cannot when it cannot */
export interface BOMFormulaVerdictEntity {
    /** '' when the formula evaluates to a number; 'Syntax error' or 'Not a number' otherwise */
    previewError: string;
    calculatedQty: string;
    /** the parser's own message, for the field's validation text */
    parseError: string;
}

export const formulaVerdictOf = (formula: string, variableKeys: string[]): BOMFormulaVerdictEntity => {
    const parseResult = parseBOMFormula(formula, variableKeys);
    const calculatedQty: string = safeEvaluateFormula(formula, TEST_SCENARIOS.standard.values, variableKeys);
    const previewError: string = parseResult.error
        ? (parseResult.error === 'Formula must give a number, not text' ? 'Not a number' : 'Syntax error')
        : (calculatedQty === 'Syntax error' || calculatedQty === 'Not a number' ? calculatedQty : '');
    return { previewError, calculatedQty, parseError: parseResult.error || '' };
};

export interface BOMFormulaEditorProps {
    value: string;
    onChange: (value: string) => void;
    /** the vocabulary of a formula as the backend defines it, each variable with how its value is found */
    variables: BOMFormulaVariableEntity[];
    functions: BOMFormulaFunctionEntity[];
    /** the validation text shows once the form was submitted */
    isSubmitted: boolean;
    error: string;
    required: boolean;
    /** shown under the field, after the validation text */
    hint?: React.ReactNode;
    /** shown above the field in its column (the BOM dialog puts the UOM there) */
    beforeFormula?: React.ReactNode;
}

/** the quantity formula editor of the BOM dialogs: highlighted field with operator chips, calculation preview, variables and functions */
export const BOMFormulaEditor: React.FC<BOMFormulaEditorProps> = ({ value, onChange, variables, functions, isSubmitted, error, required, hint, beforeFormula }) => {
    // the function picked in the FUNCTIONS list, the first one until the user picks another; its syntax and meaning show under the list
    const [selectedFunction, setSelectedFunction] = useState<string>('');
    const selectedFunctionDoc: BOMFormulaFunctionEntity | undefined = functions.find((fn) => fn.name === selectedFunction);

    useEffect(() => {
        if (IsStringNullEmptyOrUndefined(selectedFunction) && !IsNullOrEmptyArray(functions)) {
            setSelectedFunction(functions[0].name);
        }
    }, [functions, selectedFunction]);

    const variableKeys: string[] = useMemo(() => variables.map((variable) => variable.key), [variables]);
    const verdict: BOMFormulaVerdictEntity = useMemo(() => formulaVerdictOf(value, variableKeys), [value, variableKeys]);

    const scenarioValueOf = (key: string): string => {
        const scenarioValue = TEST_SCENARIOS.standard.values[key];
        return scenarioValue === undefined || scenarioValue === null ? '-' : String(scenarioValue);
    };

    // names the formula mentions, so the chips of the variables in use read as picked
    const namesInFormula: Set<string> = useMemo(() => new Set(value.match(/[A-Za-z_][A-Za-z0-9_]*/g) || []), [value]);
    const formulaBackdropRef = useRef<HTMLDivElement>(null);

    /** the formula as coloured tokens drawn under the textarea's (transparent) text; every character is kept so the two stay aligned */
    const formulaHighlight = useMemo(() => {
        const variableNames: Set<string> = new Set(variables.map((variable) => variable.key));
        const functionNames: Set<string> = new Set(functions.map((fn) => fn.name));
        const tokens: string[] = value.match(/[A-Za-z_][A-Za-z0-9_]*|\d+(?:\.\d+)?|\.\d+|"[^"]*"?|'[^']*'?|<>|<=|>=|[+\-*/=<>(),]|\s+|[\s\S]/g) || [];
        return tokens.map((token, index) => {
            let className = 'tok-op';
            if (/^\s+$/.test(token)) {
                className = '';
            } else if (/^[A-Za-z_]/.test(token)) {
                className = variableNames.has(token) ? 'tok-var' : functionNames.has(token.toUpperCase()) ? 'tok-fn' : 'tok-unknown';
            } else if (/^[\d.]/.test(token)) {
                className = 'tok-num';
            } else if (/^["']/.test(token)) {
                className = 'tok-str';
            }
            return className
                ? <span key={index} className={className}>{token}</span>
                : <React.Fragment key={index}>{token}</React.Fragment>;
        });
    }, [value, variables, functions]);

    const formulaInputRef = useRef<HTMLTextAreaElement>(null);
    // caret (or selection) to place once the formula state has re-rendered after an insert
    const pendingSelectionRef = useRef<{ start: number; end: number } | null>(null);

    useEffect(() => {
        const selection = pendingSelectionRef.current;
        const input = formulaInputRef.current;
        if (selection && input) {
            pendingSelectionRef.current = null;
            input.focus();
            input.setSelectionRange(selection.start, selection.end);
        }
    }, [value]);

    /**
     * puts the token where the caret is (replacing any selection), spaced from a neighbouring name, and leaves the caret after it;
     * `select` = the part of the token to leave highlighted instead, so a placeholder can be typed over
     */
    const insertAtCursor = (token: string, select?: { start: number; end: number }): void => {
        const input = formulaInputRef.current;
        const current: string = value;
        const start: number = input && input.selectionStart !== null ? input.selectionStart : current.length;
        const end: number = input && input.selectionEnd !== null ? input.selectionEnd : start;
        const before: string = current.slice(0, start);
        const after: string = current.slice(end);
        const spaceBefore: string = /[A-Za-z0-9_)]$/.test(before) && /^[A-Za-z0-9_(]/.test(token) ? ' ' : '';
        const spaceAfter: string = /[A-Za-z0-9_)]$/.test(token) && /^[A-Za-z0-9_(]/.test(after) ? ' ' : '';
        const insertedAt: number = before.length + spaceBefore.length;
        onChange(before + spaceBefore + token + spaceAfter + after);
        pendingSelectionRef.current = select
            ? { start: insertedAt + select.start, end: insertedAt + select.end }
            : { start: insertedAt + token.length, end: insertedAt + token.length };
    };

    /** the whole call with its placeholders, e.g. IF(condition, then, otherwise), with the first placeholder selected so only the values are left to type */
    const insertFunction = (name: string): void => {
        const fn: BOMFormulaFunctionEntity | undefined = functions.find((item) => item.name === name);
        const template: string = fn ? fn.syntax.replace(', ...', '') : name + '()';
        const open: number = template.indexOf('(');
        const close: number = template.search(/[,)]/);
        insertAtCursor(template, open !== -1 && close > open ? { start: open + 1, end: close } : undefined);
    };

    return (
        <>
            {/* Quantity Formula (Left) & Calculation Preview (Right) */}
            <div className="row mb-3">
                <div className="col-12 col-md-6 mb-3 mb-md-0">
                    {beforeFormula}
                    <div className="form-group mb-0">
                        <label className="text-muted small font-weight-bold d-flex align-items-center mb-2">
                            <span>QUANTITY FORMULA</span>
                            {required && <sup className="text-danger font-weight-bold ml-1">*</sup>}
                        </label>
                        <div className="bom-formula-editor">
                            <div className="bom-formula-backdrop" ref={formulaBackdropRef} aria-hidden="true">
                                {formulaHighlight}
                                <span>{'​'}</span>
                            </div>
                            <InputTextarea
                                ref={formulaInputRef}
                                value={value}
                                onChange={(e) => onChange(e.target.value)}
                                onScroll={(e) => {
                                    if (formulaBackdropRef.current) {
                                        formulaBackdropRef.current.scrollTop = e.currentTarget.scrollTop;
                                    }
                                }}
                                placeholder="e.g. 1*D or HINGE_QTY*D"
                                className={isSubmitted && !IsStringNullEmptyOrUndefined(error) ? 'form-control bom-formula-textarea p-invalid' : 'form-control bom-formula-textarea'}
                                rows={3}
                                autoResize={false}
                                spellCheck={false}
                            />
                        </div>
                        {isSubmitted && !IsStringNullEmptyOrUndefined(error) && (
                            <small className="text-danger font-weight-bold d-block mt-1">{error}</small>
                        )}
                        {hint}
                        <div className="bom-formula-toolbar">
                            {OPERATOR_CHIPS.map((chip) => (
                                <PillChip key={chip} label={chip} variant="operator" onClick={() => insertAtCursor(chip)} />
                            ))}
                        </div>
                    </div>
                </div>

                <div className="col-12 col-md-6">
                    <div className="bom-card mb-0">
                        <div className="bom-card-header">
                            <span className="bom-card-title">CALCULATION PREVIEW</span>
                            {!IsStringNullEmptyOrUndefined(verdict.previewError)
                                ? <span className="bom-calc-status-pill error">{verdict.previewError}</span>
                                : <span className="bom-calc-status-pill valid">Valid</span>}
                        </div>
                        <div className="bom-card-body">
                            <span className="bom-preview-label d-block mb-1">EVALUATED DEMAND QUANTITY</span>
                            <div className="d-flex align-items-baseline mb-2" style={{ gap: '8px' }}>
                                <span className={!IsStringNullEmptyOrUndefined(verdict.previewError) ? 'bom-preview-value error' : 'bom-preview-value'}>{verdict.calculatedQty}</span>
                                {IsStringNullEmptyOrUndefined(verdict.previewError) && verdict.calculatedQty !== '-' && verdict.calculatedQty !== 'Not picked' && <span className="bom-preview-unit">UOM</span>}
                            </div>
                            <div className="bom-preview-specs">
                                <div className="d-flex align-items-center justify-content-between mb-1">
                                    <span className="bom-preview-label">SIMULATION DOOR SPECS</span>
                                    <span className="bom-spec-badge">{TEST_SCENARIOS.standard.label}</span>
                                </div>
                                {/* one badge per backend variable, valued from the sample door */}
                                <div className="d-flex flex-wrap" style={{ gap: '4px' }}>
                                    {variables.map((variable) => (
                                        <span key={variable.key} className="bom-spec-badge" title={variable.label}>
                                            <strong>{variable.key}:</strong> {scenarioValueOf(variable.key)}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* variables and functions come from the backend; hovering a variable chip shows how its value is found */}
            <Tooltip target=".bom-token-chip[data-pr-tooltip]" position="top" className="bom-token-tooltip" showDelay={150} />
            <div className="row g-2">
                <div className="col-12 col-md-7 mb-2 mb-md-0">
                    <div className="bom-card h-100 mb-0">
                        <div className="bom-card-header">
                            <span className="bom-card-title">VARIABLES</span>
                            <span className="bom-card-hint">Hover a chip for how its value is found</span>
                        </div>
                        <div className="bom-card-body">
                            {IsNullOrEmptyArray(variables)
                                ? <div className="bom-chip-empty">No variables available</div>
                                : <div className="bom-chip-row">
                                    {variables.map((variable) => (
                                        <PillChip
                                            key={variable.key}
                                            label={variable.key}
                                            variant="variable"
                                            active={namesInFormula.has(variable.key)}
                                            tooltip={variable.key + ' · ' + variable.label + '\n' + variable.logic}
                                            onClick={() => insertAtCursor(variable.key)}
                                        />
                                    ))}
                                </div>}
                        </div>
                    </div>
                </div>

                <div className="col-12 col-md-5">
                    <div className="bom-card h-100 mb-0">
                        <div className="bom-card-header">
                            <span className="bom-card-title">FUNCTIONS</span>
                            <span className="bom-card-hint">Pick one, then Insert</span>
                        </div>
                        <div className="bom-card-body">
                            <Dropdown
                                value={IsStringNullEmptyOrUndefined(selectedFunction) ? null : selectedFunction}
                                options={functions}
                                optionLabel="name"
                                optionValue="name"
                                placeholder={IsNullOrEmptyArray(functions) ? 'No functions available' : 'Select function'}
                                disabled={IsNullOrEmptyArray(functions)}
                                className="w-100"
                                panelClassName="bom-fn-dd-panel"
                                itemTemplate={(fn: BOMFormulaFunctionEntity) => (
                                    <div className="bom-fn-option">
                                        <span className="bom-fn-name">{fn.name}</span>
                                        <span className="bom-fn-syntax">{fn.syntax}</span>
                                    </div>
                                )}
                                onChange={(e) => setSelectedFunction(e.value || '')}
                            />
                            {selectedFunctionDoc && (
                                <div className="bom-fn-detail">
                                    <div className="d-flex align-items-center justify-content-between" style={{ gap: '8px' }}>
                                        <span className="bom-fn-syntax">{selectedFunctionDoc.syntax}</span>
                                        <button type="button" className="bom-fn-insert" onClick={() => insertFunction(selectedFunction)}>
                                            <i className="pi pi-plus"></i> Insert
                                        </button>
                                    </div>
                                    <div className="bom-fn-logic">{selectedFunctionDoc.logic}</div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};
